import { Alert, AlertTitle, Tab, Tabs } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useRouteUrl } from '../../hooks/use-route-url';
import styled from 'styled-components';
import { ClinicDetailsTabType } from '../../common/model/type/clinic-details-tab.type';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import Colours from '../../design-system/colours';
import Variables from '../../design-system/variables';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvDateParser from '../UI/atoms/OvDateParser';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvRecordDetailsPage from '../UI/atoms/OvRecordDetailsPage';
import OvStatusChip from '../UI/atoms/OvStatusChip';
import DeleteIcon from '@mui/icons-material/Delete';
import OvConfirmationDialog from '../UI/molecules/OvConfirmationDialog';
import OvCopyToClipboardButton from '../UI/molecules/OvCopyToClipboardButton';
import OvRecordDetailsErrorState from '../UI/molecules/OvRecordDetailsErrorState';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';

import ClinicInfo from './ClinicInfo';
import { Resource } from '../../common/model/type/resource.enum';
import useAuthorized from '../../hooks/use-authorized';
import OvClinicAuditActivity from '../UI/organisms/OvClinicAuditActivity';
import { AdminClinicService } from '../../services/admin/admin-clinic.service';
import { useQuery } from '@tanstack/react-query';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvBackButton from '../UI/atoms/OvBackButton';
import { useAdminClinicListMutations } from '../../hooks/use-admin-clinic-mutations';

const CLINIC_LIST_PATH = '/clinics';

const ClinicDetails = () => {
  const { t } = useTranslation();
  const { clinicId, '*': tabPath = '' } = useParams() as {
    clinicId: string;
    '*'?: string;
  };
  const url = useRouteUrl();

  const applicationOperations = useAuthorized(Resource.Application);
  const clinicOperations = useAuthorized(Resource.Clinic);
  const clinicAuditLogActivityOperations = useAuthorized(
    Resource.ClinicAuditLogActivity
  );

  const clinicDeleteAllowed =
    clinicOperations.delete || applicationOperations.supervise;

  const clinicAuditLogActivityReadAllowed =
    clinicAuditLogActivityOperations.read || applicationOperations.supervise;

  const [isOpenDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] =
    useState<boolean>(false);

  const navigate = useNavigate();

  const {
    data: clinic,
    isFetching: isGetClinicFetching,
    error: getClinicError,
    refetch,
  } = useQuery({
    queryKey: ['admin-clinic', clinicId],
    queryFn: () => AdminClinicService.getClinicById(clinicId),
    enabled: !!clinicId,
  });

  const { deleteClinic, updateClinic } = useAdminClinicListMutations();

  const activeTab =
    tabPath.split('/')[0] === ClinicDetailsTabType.AUDIT_ACTIVITY
      ? ClinicDetailsTabType.AUDIT_ACTIVITY
      : ClinicDetailsTabType.CLINIC_INFO;

  const handleDeleteClinic = () => {
    setOpenDeleteConfirmationDialog(false);
    deleteClinic.mutate(clinicId, {
      onSuccess: () => navigate(CLINIC_LIST_PATH),
    });
  };

  if (!clinic) {
    return (
      <OvRecordDetailsPage>
        {getClinicError ? (
          <OvRecordDetailsErrorState
            title={t('clinics.details.errorTitle')}
            error={getClinicError}
            onRetry={() => refetch()}
            backLabel={t('app.subPages.menu.clinics')}
            backPath={CLINIC_LIST_PATH}
          />
        ) : (
          <OvLoadingIndicator position="fixed" />
        )}
      </OvRecordDetailsPage>
    );
  }

  const mutationError =
    getClinicError ?? deleteClinic.error ?? updateClinic.error;
  const isLoading =
    isGetClinicFetching || deleteClinic.isPending || updateClinic.isPending;
  const clinicDisplayId = clinic.document_id ?? clinic.id ?? '';

  return (
    <OvRecordDetailsPage aria-busy={isLoading}>
      {mutationError && (
        <OvErrorMessage message={getAxiosErrorMessage(mutationError)} />
      )}

      {clinic.is_demo_clinic && (
        <StyledAlert severity="warning">
          <AlertTitle>{t('clinicDetails.demoAlertTitle')}</AlertTitle>
          {t('clinicDetails.demoAlertDesc')}
        </StyledAlert>
      )}

      <OvRecordDetailsCanvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('clinics.details.breadcrumb')}
          backControl={
            <OvBackButton
              label={t('app.subPages.menu.clinics')}
              fallbackPath={CLINIC_LIST_PATH}
            />
          }
          current={clinic.name}
          title={clinic.name}
          titleAside={
            clinic.is_demo_clinic && (
              <OvStatusChip label={t('userList.demo')} tone="caution" />
            )
          }
          meta={
            <>
              <span>
                {clinicDisplayId}
                <OvCopyToClipboardButton
                  text={clinicDisplayId}
                  label={t('clinics.details.copyId')}
                />
              </span>
              {clinic.created_at && (
                <span>
                  <OvDateParser
                    date={clinic.created_at}
                    format="MMM DD, YYYY - hh:mm A"
                  />
                </span>
              )}
            </>
          }
          actions={
            clinicDeleteAllowed && (
              <OvRecordHeaderActions>
                <OvCompactButton
                  tone="danger"
                  icon={<DeleteIcon />}
                  onClick={() => setOpenDeleteConfirmationDialog(true)}
                >
                  {t('common.actions.delete')}
                </OvCompactButton>
              </OvRecordHeaderActions>
            )
          }
        />

        <TabContainer>
          <Tabs value={activeTab} variant="scrollable">
            <Tab
              value={ClinicDetailsTabType.CLINIC_INFO}
              label={t('clinicDetails.tabs.clinicInfo')}
              component={Link}
              to={`${url}/${ClinicDetailsTabType.CLINIC_INFO}`}
            />
            {clinicAuditLogActivityReadAllowed && (
              <Tab
                value={ClinicDetailsTabType.AUDIT_ACTIVITY}
                label={t('clinicDetails.tabs.auditActivity')}
                component={Link}
                to={`${url}/${ClinicDetailsTabType.AUDIT_ACTIVITY}`}
              />
            )}
          </Tabs>
        </TabContainer>

        <Routes>
          <Route
            index
            element={
              <Navigate
                to={`${url}/${ClinicDetailsTabType.CLINIC_INFO}`}
                replace
              />
            }
          />
          <Route
            path="clinic-info/*"
            element={
              <ClinicInfo
                selectedClinic={clinic}
                updateClinicMutation={updateClinic}
              />
            }
          />
          <Route
            path="audit-activity/*"
            element={
              <TabContent>
                <OvClinicAuditActivity clinicId={clinic.id as string} />
              </TabContent>
            }
          />
        </Routes>
      </OvRecordDetailsCanvas>

      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={isOpenDeleteConfirmationDialog}
        onCancel={() => setOpenDeleteConfirmationDialog(false)}
        onConfirm={handleDeleteClinic}
        title="clinicDetails.actions.deleteTitle"
        description="clinicDetails.actions.deleteDescription"
      />

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </OvRecordDetailsPage>
  );
};

export default ClinicDetails;

const TabContainer = styled.div`
  padding: 0 1rem;
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  && {
    .MuiTab-root {
      text-transform: none;
    }

    .MuiTabs-indicator {
      background-color: ${Colours.OV_BASE};
    }
  }
`;

const TabContent = styled.div`
  padding: 1.25rem 1.5rem;
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;

const StyledAlert = styled(Alert)`
  && {
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_YELLOW};
    color: ${Colours.OV_BASE};

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;
